import { useCallback, useEffect, useRef, type FC } from "react";
import { useForm } from "react-hook-form";
import styled from "styled-components";

import { useRootDispatch, useRootSelector } from "@ds/hooks";

import {
  selectCampaignById,
  selectCampaigns,
  selectCampaignsError,
  selectCampaignsIsLoading,
  selectSelectedCampaignIds,
} from "@ds/modules/content/campaigns/redux/selectors";
import { campaignsActions } from "@ds/modules/content/campaigns/redux/slice";

import { Button, ButtonActionType, ButtonType } from "@ds/components/forms/buttons";
import { InputText } from "@ds/components/forms/inputs";
import { LabelWrapper } from "@ds/components/forms/labels";
import { IconType } from "@ds/components/icons";
import { Dialog } from "@ds/components/modals";

const Form = styled.form`
  display: flex;
  flex-direction: column;
  min-width: 400px;
`;

export const DuplicateCampaignModal: FC<{
  onClose: () => void;
  campaignId?: number;
  onSuccess?: () => void;
}> = ({ onClose, campaignId: campaignIdProp, onSuccess }) => {
  const selectedCampaignId = useRootSelector(selectSelectedCampaignIds)[0];
  const campaignId = campaignIdProp ?? selectedCampaignId;
  const campaign = useRootSelector(state => selectCampaignById(state, campaignId));
  const campaigns = useRootSelector(selectCampaigns);
  const campaignNames = campaigns
    .filter(_campaign => _campaign.name)
    .map(_campaign => _campaign.name.trim().toLowerCase());
  const isLoading = useRootSelector(selectCampaignsIsLoading);
  const error = useRootSelector(selectCampaignsError);
  const hasSubmitted = useRef(false);
  const dispatch = useRootDispatch();

  useEffect(() => {
    if (hasSubmitted.current && !isLoading) {
      if (!error) {
        onSuccess?.();
      }
      onClose();
    }
  }, [isLoading, error, onClose, onSuccess]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{ name: string }>({
    defaultValues: {
      name: campaign?.name ? `${campaign.name} (Copy)` : "",
    },
  });

  const onSubmit = useCallback(
    (data: { name: string }) => {
      if (campaignId === undefined || campaignId === null) return;
      hasSubmitted.current = true;
      dispatch(campaignsActions.duplicateCampaign({ campaignId, newTitle: data.name }));
    },
    [dispatch, campaignId],
  );

  // NOTE: Name uniqueness is validated against the currently loaded campaigns in the Redux store
  // (typically one page of results). Names outside the current page are invisible to this check,
  // so duplicate names on other pages will only be caught by the API error response.
  const { ref, ...nameRegister } = register("name", {
    required: "Name is required",
    validate: value => {
      const trimmed = value?.trim() ?? "";
      if (!trimmed) return true;
      const isDuplicate = campaignNames.some(n => n.trim().toLowerCase() === trimmed.toLowerCase());
      return isDuplicate ? "A campaign with this name already exists" : true;
    },
  });

  const renderFooter = useCallback(
    () => (
      <>
        <Button
          label="Cancel"
          ariaLabel="cancel button"
          buttonType={ButtonType.Outlined}
          actionType={ButtonActionType.Secondary}
          icon={IconType.Close}
          iconPosition="right"
          isDisabled={isLoading}
          onClick={onClose}
        />
        <Button
          label="Duplicate"
          ariaLabel="duplicate button"
          buttonType={ButtonType.Raised}
          icon={IconType.Duplicate}
          iconPosition="right"
          isLoading={isLoading}
          isDisabled={campaignId === undefined || campaignId === null}
          onClick={handleSubmit(onSubmit)}
        />
      </>
    ),
    [campaignId, isLoading, onClose, handleSubmit, onSubmit],
  );

  return (
    <Dialog modal visible header="Duplicate Campaign" footer={renderFooter} onHide={onClose}>
      <Form onSubmit={handleSubmit(onSubmit)} noValidate>
        <LabelWrapper label="New Campaign Name" isRequired error={errors.name}>
          <InputText
            inputRef={ref}
            placeholder="Enter a new name for the duplicated campaign"
            {...nameRegister}
            value={watch("name") ?? ""}
          />
        </LabelWrapper>
      </Form>
    </Dialog>
  );
};
